import type { components } from "../../api/generated-schema";
import { apiRequest, createRequestId } from "../../api/httpClient";
import { formatDateVn } from "../../lib/dateFormat";
import { label } from "../../lib/labels";

export type AuthorizationCatalogPublished = components["schemas"]["AuthorizationCatalogPublished"];
export type CatalogRoleOption = components["schemas"]["CatalogRoleOption"];
export type RoleAssignmentPage = components["schemas"]["RoleAssignmentPage"];
export type RoleAssignmentItem = components["schemas"]["RoleAssignmentItem"];
export type RoleAssignmentCommandResult = components["schemas"]["RoleAssignmentCommandResult"];
export type EffectivePermissionsPreview = components["schemas"]["EffectivePermissionsPreview"];
export type EffectivePermissionGrant = components["schemas"]["EffectivePermissionGrant"];
export type GrantRoleAssignmentInput = components["schemas"]["GrantRoleAssignmentRequest"];

/** Nhị phân suy ra từ catalog — frontend hiển thị theo, backend vẫn là thực quyền. */
export const ROLE_ASSIGNMENT_READ_ACTION = "ROLE_ASSIGNMENT_READ";
export const ROLE_ASSIGNMENT_GRANT_ACTION = "ROLE_ASSIGNMENT_GRANT";
export const ROLE_ASSIGNMENT_REVOKE_ACTION = "ROLE_ASSIGNMENT_REVOKE";

/** Resource scope SYSTEM theo DEC-021/D5: nil UUID, đứng một mình. */
export const SYSTEM_SCOPE_TYPE = "SYSTEM";
export const SYSTEM_SCOPE_ID = "00000000-0000-0000-0000-000000000000";

export type AssignmentState = NonNullable<RoleAssignmentCommandResult["state"]>;

const ACCOUNTS_BASE = "/admin/user-accounts";

function commandHeaders(idempotencyKey: string): Record<string, string> {
  return { "Idempotency-Key": idempotencyKey, "X-Request-Id": createRequestId() };
}

function queryHeaders(): Record<string, string> {
  return { "X-Request-Id": createRequestId() };
}

function signalInit(signal?: AbortSignal): RequestInit {
  return signal ? { signal } : {};
}

function encodeQuery(entries: Array<[string, string]>): string {
  return entries.map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join("&");
}

export function getCurrentAuthorizationCatalog(
  signal?: AbortSignal,
): Promise<AuthorizationCatalogPublished> {
  return apiRequest<AuthorizationCatalogPublished>("/admin/authorization-catalog/current", {
    headers: queryHeaders(),
    ...signalInit(signal),
  });
}

export function listUserRoleAssignments(
  accountId: string,
  params: { page: number; size: number; asOf?: string },
  signal?: AbortSignal,
): Promise<RoleAssignmentPage> {
  const entries: Array<[string, string]> = [
    ["page", String(params.page)],
    ["size", String(params.size)],
  ];
  if (params.asOf) entries.push(["asOf", params.asOf]);
  return apiRequest<RoleAssignmentPage>(
    `${ACCOUNTS_BASE}/${encodeURIComponent(accountId)}/role-assignments?${encodeQuery(entries)}`,
    { headers: queryHeaders(), ...signalInit(signal) },
  );
}

export function grantUserRoleAssignment(
  accountId: string,
  body: GrantRoleAssignmentInput,
  idempotencyKey: string,
): Promise<RoleAssignmentCommandResult> {
  return apiRequest<RoleAssignmentCommandResult>(
    `${ACCOUNTS_BASE}/${encodeURIComponent(accountId)}/role-assignments`,
    {
      method: "POST",
      headers: commandHeaders(idempotencyKey),
      body: JSON.stringify(body),
    },
  );
}

export function revokeUserRoleAssignment(
  accountId: string,
  assignmentId: string,
  body: { expectedVersion: number; reason: string },
  idempotencyKey: string,
): Promise<RoleAssignmentCommandResult> {
  return apiRequest<RoleAssignmentCommandResult>(
    `${ACCOUNTS_BASE}/${encodeURIComponent(accountId)}/role-assignments`
      + `/${encodeURIComponent(assignmentId)}/revoke`,
    {
      method: "POST",
      headers: commandHeaders(idempotencyKey),
      body: JSON.stringify(body),
    },
  );
}

export function previewUserEffectivePermissions(
  accountId: string,
  asOf?: string,
  signal?: AbortSignal,
): Promise<EffectivePermissionsPreview> {
  const query = asOf ? `?${encodeQuery([["asOf", asOf]])}` : "";
  return apiRequest<EffectivePermissionsPreview>(
    `${ACCOUNTS_BASE}/${encodeURIComponent(accountId)}/effective-permissions${query}`,
    { headers: queryHeaders(), ...signalInit(signal) },
  );
}

// ---------------------------------------------------------------------------
// Nhãn nghiệp vụ (IRM SCR-052 §8.2): role/action code, UUID, version,
// fingerprint là NEVER_USER_UI — UI chỉ render các map dưới đây; giá trị lạ
// không suy diễn tên, dùng nhãn chung để không lộ mã nội bộ.
// ---------------------------------------------------------------------------

export const ASSIGNMENT_STATE_LABEL: Readonly<Record<AssignmentState, string>> = {
  SCHEDULED: "Chưa tới hiệu lực",
  EFFECTIVE: "Đang hiệu lực",
  ENDED: "Đã kết thúc",
  CANCELLED: "Đã hủy",
};

export const ASSIGNMENT_BADGE_STATUS: Readonly<
  Record<AssignmentState, "default" | "processing" | "success" | "warning">
> = {
  SCHEDULED: "processing",
  EFFECTIVE: "success",
  ENDED: "default",
  CANCELLED: "warning",
};

export const ACTION_DESCRIPTIONS: Readonly<Record<string, string>> = {
  ORGANIZATION_READ: "Xem đơn vị",
  ORGANIZATION_CREATE: "Tạo đơn vị",
  ORGANIZATION_OPEN: "Mở đơn vị",
  ORGANIZATION_CLOSE: "Đóng đơn vị",
  ORGANIZATION_RENAME: "Đổi tên đơn vị",
  ORGANIZATION_TRANSFER: "Chuyển đơn vị",
  ORGANIZATION_MERGE: "Sáp nhập đơn vị",
  ADMIN_AREA_READ: "Xem địa bàn",
  ADMIN_AREA_CREATE: "Tạo địa bàn",
  ADMIN_AREA_OPEN: "Mở địa bàn",
  ADMIN_AREA_CLOSE: "Đóng địa bàn",
  ADMIN_AREA_RENAME: "Đổi tên địa bàn",
  ADMIN_AREA_TRANSFER: "Chuyển địa bàn",
  ADMIN_AREA_MERGE: "Sáp nhập địa bàn",
  USER_ACCOUNT_READ: "Xem tài khoản",
  USER_ACCOUNT_CREATE: "Tạo tài khoản",
  USER_ACCOUNT_ACTIVATE: "Kích hoạt tài khoản",
  USER_ACCOUNT_LOCK: "Khóa tài khoản",
  USER_ACCOUNT_UNLOCK: "Mở khóa tài khoản",
  USER_ACCOUNT_PROVISION: "Thiết lập đăng nhập",
  USER_ACCOUNT_PROFILE_SYNC: "Đồng bộ hồ sơ đăng nhập",
  USER_ACCOUNT_PASSWORD_RESET: "Đặt lại mật khẩu",
  USER_ACCOUNT_IDENTITY_LINK: "Liên kết danh tính",
  ROLE_ASSIGNMENT_READ: "Xem phân quyền",
  ROLE_ASSIGNMENT_GRANT: "Cấp vai trò",
  ROLE_ASSIGNMENT_GRANTED: "Cấp vai trò",
  ROLE_ASSIGNMENT_REVOKE: "Thu hồi vai trò",
  ROLE_ASSIGNMENT_REVOKED: "Thu hồi vai trò",
  DATA_READ: "Đọc dữ liệu",
  DATA_WRITE: "Cập nhật dữ liệu",
  SENSITIVE_FIELD_READ: "Xem thông tin nhạy cảm",
  DOCUMENT_READ: "Đọc tài liệu",
  EXPORT_EXECUTE: "Xuất dữ liệu",
  AUDIT_READ: "Xem nhật ký",
  AUDIT_SEARCH: "Tra cứu nhật ký",
  AUDIT_DETAIL_READ: "Xem chi tiết nhật ký",
  AUDIT_EXPORT: "Xuất nhật ký",
  LOGIN_SUCCESS: "Đăng nhập thành công",
  LOGIN_FAILED: "Đăng nhập thất bại",
  AUTHENTICATION_DENIED: "Từ chối xác thực",
};

export function describeAction(code: string, displayName?: unknown): string {
  if (typeof displayName === "string" && displayName.trim().length > 0) {
    return displayName.trim();
  }
  return ACTION_DESCRIPTIONS[code.trim().toUpperCase()] ?? label(code, displayName);
}

/** Suy trạng thái hiệu lực từ khoảng UTC — cùng ngữ nghĩa [from, to) với backend. */
export function deriveAssignmentState(
  item: RoleAssignmentItem,
  now: Date = new Date(),
): AssignmentState {
  if (item.validFrom === item.validTo) return "CANCELLED";
  const from = Date.parse(item.validFrom);
  if (now.getTime() < from) return "SCHEDULED";
  if (item.validTo == null || now.getTime() < Date.parse(item.validTo)) return "EFFECTIVE";
  return "ENDED";
}

/** Actor có role nào trong catalog được cấp action đang xét hay không (UX hint). */
export function actorHasAction(
  roles: readonly CatalogRoleOption[],
  actorRoleCodes: readonly string[],
  action: string,
): boolean {
  return roles.some(
    (role) => actorRoleCodes.includes(role.code) && role.actions.includes(action),
  );
}

/** Grant SYSTEM (nil UUID đứng một mình) là "Toàn hệ thống" theo DEC-021. */
export function isSystemScope(item: RoleAssignmentItem): boolean {
  return (item.dataScopes ?? []).some(
    (scope) => scope.scopeType === SYSTEM_SCOPE_TYPE
      && scope.scopeId?.toUpperCase() === SYSTEM_SCOPE_ID.toUpperCase(),
  );
}

/** Nhãn phạm vi bằng tên nghiệp vụ; UUID lạ suy diễn thành nhãn chung, không lộ mã. */
export function scopeSummary(
  item: RoleAssignmentItem,
  orgNames: ReadonlyMap<string, string>,
  areaNames: ReadonlyMap<string, string>,
): string {
  if (isSystemScope(item)) {
    return "Toàn hệ thống";
  }
  const organization = item.organizationId ? orgNames.get(item.organizationId) : undefined;
  const area = item.administrativeAreaId ? areaNames.get(item.administrativeAreaId) : undefined;
  if (organization && area) return `${organization} · ${area}`;
  if (organization) return organization;
  if (area) return area;
  if ((item.dataScopes ?? []).length > 0) return "Theo hồ sơ được chỉ định";
  return "Phạm vi không xác định";
}

/** `dd-mm-yyyy` từ mốc ISO — theo quy ước hiển thị ngày dùng chung (`lib/dateFormat`). */
export function formatAssignmentDate(iso: string | null | undefined): string {
  return iso ? formatDateVn(iso) : "";
}
